// Вход через Google на десктопе — своим попап-окном OAuth (response_type=id_token),
// как вход через Apple: окно открывает НАШ клик, без GIS-кнопки и без FedCM.
// В десктопном Chrome GIS-кнопка на lavinook.com уходит в FedCM и при отказе
// FedCM клик молча ничего не делает (проверено в Chrome пользователя), а на
// мобильном UA та же кнопка открывает обычный попап — поэтому мобилку не трогаем.
//
// Поток: window.open → accounts.google.com/o/oauth2/v2/auth → Google редиректит
// попап на `${origin}/#id_token=…&state=…` → ранний обработчик в index.jsx
// (до рендера приложения) передаёт токен в основное окно через BroadcastChannel/
// localStorage и закрывает попап. Если попап заблокирован — тот же поток
// редиректом в текущей вкладке (токен подхватывает экран входа).
// redirect_uri должен быть зарегистрирован у Google-клиента (lavinook.com — есть).

const CHANNEL = "velvet-google-auth";
const STATE_KEY = "googleAuthState";
const MODE_KEY = "googleAuthMode";
const RESULT_KEY = "googleAuthResult";
const PENDING_KEY = "googlePendingIdToken";

const allowedOrigins = (): string[] =>
  (process.env.REACT_APP_GOOGLE_REDIRECT_ORIGINS || "https://lavinook.com")
    .split(",").map((s) => s.trim()).filter(Boolean);

export const canUseGooglePopup = (): boolean =>
  !!process.env.REACT_APP_GOOGLE_ID && allowedOrigins().includes(window.location.origin);

const rnd = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const buildUrl = (state: string, nonce: string) => {
  const p = new URLSearchParams({
    client_id: `${process.env.REACT_APP_GOOGLE_ID}.apps.googleusercontent.com`,
    redirect_uri: window.location.origin,
    response_type: "id_token",
    scope: "openid email profile",
    nonce,
    state,
    prompt: "select_account",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${p.toString()}`;
};

export type GoogleResult = { idToken?: string; error?: string };

/** Открыть попап входа Google. onResult вызывается с id_token (или ошибкой). Возвращает отписку. */
export const startGooglePopup = (onResult: (r: GoogleResult) => void): (() => void) => {
  const state = rnd();
  const nonce = rnd();
  try {
    window.localStorage.setItem(STATE_KEY, state);
    window.localStorage.removeItem(RESULT_KEY);
  } catch (e) { /* noop */ }
  const url = buildUrl(state, nonce);

  let done = false;
  const finish = (data: any) => {
    if (done || !data || data.state !== state) return;
    done = true;
    cleanup();
    onResult({ idToken: data.idToken, error: data.error });
  };
  let bc: BroadcastChannel | null = null;
  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => finish(e.data);
  } catch (e) { bc = null; }
  const onStorage = (e: StorageEvent) => {
    if (e.key === RESULT_KEY && e.newValue) {
      try { finish(JSON.parse(e.newValue)); } catch (err) { /* noop */ }
    }
  };
  window.addEventListener("storage", onStorage);
  const cleanup = () => {
    window.removeEventListener("storage", onStorage);
    try { bc && bc.close(); } catch (e) { /* noop */ }
  };

  const w = 500, h = 640;
  const left = Math.max(0, (window.screenX || 0) + (window.outerWidth - w) / 2);
  const top = Math.max(0, (window.screenY || 0) + (window.outerHeight - h) / 2);
  try { window.localStorage.setItem(MODE_KEY, "popup"); } catch (e) { /* noop */ }
  const popup = window.open(url, "velvet_google_signin", `width=${w},height=${h},left=${left},top=${top}`);
  if (!popup) {
    // попап заблокирован — тем же потоком в текущей вкладке
    cleanup();
    try { window.localStorage.setItem(MODE_KEY, "redirect"); } catch (e) { /* noop */ }
    window.location.assign(url);
  }
  return cleanup;
};

/**
 * Вызывать в index.jsx ДО рендера. true — это окно-попап с ответом Google:
 * приложение рендерить не нужно (попап закроется).
 */
export const handleGoogleRedirectEarly = (): boolean => {
  const hash = window.location.hash || "";
  if (!/[#&](id_token|error)=/.test(hash)) return false;
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const state = params.get("state") || "";
  let expected = "";
  let mode = "popup";
  try {
    expected = window.localStorage.getItem(STATE_KEY) || "";
    mode = window.localStorage.getItem(MODE_KEY) || "popup";
  } catch (e) { /* noop */ }
  if (!state || state !== expected) return false;
  const data = { state, idToken: params.get("id_token") || undefined, error: params.get("error") || undefined };

  if (mode === "redirect") {
    try {
      window.localStorage.removeItem(STATE_KEY);
      data.idToken && window.sessionStorage.setItem(PENDING_KEY, data.idToken);
    } catch (e) { /* noop */ }
    window.history.replaceState(null, "", "/auth/phone");
    return false;
  }

  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(data);
    bc.close();
  } catch (e) { /* noop */ }
  try {
    window.localStorage.setItem(RESULT_KEY, JSON.stringify({ ...data, t: Date.now() }));
    window.localStorage.removeItem(STATE_KEY);
  } catch (e) { /* noop */ }
  try { window.opener && window.opener.focus(); } catch (e) { /* noop */ }
  window.close();
  // если окно не закрылось — пустая страница с подсказкой
  document.title = "Velvet";
  document.body.innerHTML = '<p style="font-family:sans-serif;text-align:center;margin-top:40vh;color:#687386">Можно закрыть это окно</p>';
  return true;
};

/** id_token после входа редиректом (попап был заблокирован). */
export const takePendingGoogleIdToken = (): string | null => {
  try {
    const t = window.sessionStorage.getItem(PENDING_KEY);
    t && window.sessionStorage.removeItem(PENDING_KEY);
    return t;
  } catch (e) {
    return null;
  }
};
